'use client'

import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react'

// Navigation UI State Types
interface NavigationUIState {
  // Mobile navigation state
  isMobileMenuOpen: boolean
  isBottomTabBarVisible: boolean

  // Desktop navigation state
  activeDropdown: string | null
  isSearchOpen: boolean

  // User preferences
  isNavigationCollapsed: boolean
  preferredTokenDisplay: 'both' | 'nt' | 'pt'
  enableNotificationSounds: boolean
  enableAnimations: boolean

  // Analytics tracking
  lastNavigationAction: string | null
  navigationSessionId: string
  interactionCount: number
}

// Navigation Analytics Event Types
interface NavigationAnalyticsEvent {
  type: 'navigation_click' | 'search_open' | 'dropdown_open' | 'mobile_menu_toggle' | 'tab_switch'
  target: string
  timestamp: Date
  sessionId: string
  metadata?: Record<string, any>
}

// Context Interface
interface NavigationStateContextType {
  // UI State
  uiState: NavigationUIState
  setUIState: (updates: Partial<NavigationUIState>) => void

  // Mobile Navigation Controls
  toggleMobileMenu: () => void
  closeMobileMenu: () => void
  setBottomTabBarVisibility: (visible: boolean) => void

  // Desktop Navigation Controls
  setActiveDropdown: (dropdown: string | null) => void
  toggleSearch: () => void
  openSearch: () => void
  closeSearch: () => void

  // User Preferences
  toggleNavigationCollapse: () => void
  setTokenDisplayPreference: (preference: 'both' | 'nt' | 'pt') => void
  toggleNotificationSounds: () => void
  toggleAnimations: () => void

  // Analytics
  trackNavigationEvent: (event: Omit<NavigationAnalyticsEvent, 'timestamp' | 'sessionId'>) => void
  getNavigationAnalytics: () => NavigationAnalyticsEvent[]
  clearAnalytics: () => void
}

const NavigationStateContext = createContext<NavigationStateContextType | null>(null)

// Generate unique session ID
const generateSessionId = () => {
  return `nav-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

// Default UI state
const getDefaultUIState = (): NavigationUIState => ({
  isMobileMenuOpen: false,
  isBottomTabBarVisible: true,
  activeDropdown: null,
  isSearchOpen: false,
  isNavigationCollapsed: false,
  preferredTokenDisplay: 'both',
  enableNotificationSounds: true,
  enableAnimations: true,
  lastNavigationAction: null,
  navigationSessionId: generateSessionId(),
  interactionCount: 0
})

// Load preferences from localStorage
const loadUserPreferences = (): Partial<NavigationUIState> => {
  if (typeof window === 'undefined') return {}

  try {
    const saved = localStorage.getItem('fantasy-xi-navigation-preferences')
    if (saved) {
      const preferences = JSON.parse(saved)
      return {
        isNavigationCollapsed: preferences.isNavigationCollapsed || false,
        preferredTokenDisplay: preferences.preferredTokenDisplay || 'both',
        enableNotificationSounds: preferences.enableNotificationSounds !== false,
        enableAnimations: preferences.enableAnimations !== false
      }
    }
  } catch (error) {
    console.warn('Failed to load navigation preferences:', error)
  }

  return {}
}

// Save preferences to localStorage
const saveUserPreferences = (state: NavigationUIState) => {
  if (typeof window === 'undefined') return

  try {
    const preferences = {
      isNavigationCollapsed: state.isNavigationCollapsed,
      preferredTokenDisplay: state.preferredTokenDisplay,
      enableNotificationSounds: state.enableNotificationSounds,
      enableAnimations: state.enableAnimations
    }
    localStorage.setItem('fantasy-xi-navigation-preferences', JSON.stringify(preferences))
  } catch (error) {
    console.warn('Failed to save navigation preferences:', error)
  }
}

interface NavigationStateProviderProps {
  children: ReactNode
}

export function NavigationStateProvider({ children }: NavigationStateProviderProps) {
  // Initialize state with saved preferences
  const [uiState, setUIStateInternal] = useState<NavigationUIState>(() => {
    const defaults = getDefaultUIState()
    const savedPreferences = loadUserPreferences()
    return { ...defaults, ...savedPreferences }
  })

  // Analytics storage
  const [analyticsEvents, setAnalyticsEvents] = useState<NavigationAnalyticsEvent[]>([])

  // Update UI state and save preferences
  const setUIState = useCallback((updates: Partial<NavigationUIState>) => {
    setUIStateInternal((prev) => {
      const newState = {
        ...prev,
        ...updates,
        interactionCount: prev.interactionCount + 1,
        lastNavigationAction: updates.lastNavigationAction || prev.lastNavigationAction
      }

      // Save preferences to localStorage
      saveUserPreferences(newState)

      return newState
    })
  }, [])

  // Analytics
  const trackNavigationEvent = useCallback(
    (event: Omit<NavigationAnalyticsEvent, 'timestamp' | 'sessionId'>) => {
      const fullEvent: NavigationAnalyticsEvent = {
        ...event,
        timestamp: new Date(),
        sessionId: uiState.navigationSessionId
      }

      setAnalyticsEvents((prev) => {
        // Keep only last 100 events to prevent memory issues
        const updated = [...prev, fullEvent].slice(-100)

        // Optionally send to analytics service here
        if (process.env.NODE_ENV === 'development') {
          console.log('Navigation Analytics:', fullEvent)
        }

        return updated
      })
    },
    [uiState.navigationSessionId]
  )

  // Mobile Navigation Controls
  const toggleMobileMenu = useCallback(() => {
    setUIState({
      isMobileMenuOpen: !uiState.isMobileMenuOpen,
      lastNavigationAction: 'mobile_menu_toggle'
    })
    trackNavigationEvent({
      type: 'mobile_menu_toggle',
      target: 'hamburger_menu',
      metadata: { isOpen: !uiState.isMobileMenuOpen }
    })
  }, [uiState.isMobileMenuOpen])

  const closeMobileMenu = useCallback(() => {
    setUIState({
      isMobileMenuOpen: false,
      lastNavigationAction: 'mobile_menu_close'
    })
  }, [])

  const setBottomTabBarVisibility = useCallback((visible: boolean) => {
    setUIState({
      isBottomTabBarVisible: visible,
      lastNavigationAction: 'tab_bar_visibility_change'
    })
  }, [])

  // Desktop Navigation Controls
  const setActiveDropdown = useCallback((dropdown: string | null) => {
    setUIState({
      activeDropdown: dropdown,
      lastNavigationAction: dropdown ? 'dropdown_open' : 'dropdown_close'
    })

    if (dropdown) {
      trackNavigationEvent({
        type: 'dropdown_open',
        target: dropdown,
        metadata: { dropdownType: 'desktop_navigation' }
      })
    }
  }, [])

  const toggleSearch = useCallback(() => {
    const newSearchState = !uiState.isSearchOpen
    setUIState({
      isSearchOpen: newSearchState,
      lastNavigationAction: newSearchState ? 'search_open' : 'search_close'
    })

    if (newSearchState) {
      trackNavigationEvent({
        type: 'search_open',
        target: 'search_command',
        metadata: { trigger: 'manual_toggle' }
      })
    }
  }, [uiState.isSearchOpen])

  const openSearch = useCallback(() => {
    setUIState({
      isSearchOpen: true,
      lastNavigationAction: 'search_open'
    })
    trackNavigationEvent({
      type: 'search_open',
      target: 'search_command',
      metadata: { trigger: 'direct_open' }
    })
  }, [])

  const closeSearch = useCallback(() => {
    setUIState({
      isSearchOpen: false,
      lastNavigationAction: 'search_close'
    })
  }, [])

  // User Preferences
  const toggleNavigationCollapse = useCallback(() => {
    setUIState({
      isNavigationCollapsed: !uiState.isNavigationCollapsed,
      lastNavigationAction: 'navigation_collapse_toggle'
    })
  }, [uiState.isNavigationCollapsed])

  const setTokenDisplayPreference = useCallback((preference: 'both' | 'nt' | 'pt') => {
    setUIState({
      preferredTokenDisplay: preference,
      lastNavigationAction: 'token_display_preference_change'
    })
  }, [])

  const toggleNotificationSounds = useCallback(() => {
    setUIState({
      enableNotificationSounds: !uiState.enableNotificationSounds,
      lastNavigationAction: 'notification_sounds_toggle'
    })
  }, [uiState.enableNotificationSounds])

  const toggleAnimations = useCallback(() => {
    setUIState({
      enableAnimations: !uiState.enableAnimations,
      lastNavigationAction: 'animations_toggle'
    })
  }, [uiState.enableAnimations])

  const getNavigationAnalytics = useCallback(() => {
    return analyticsEvents
  }, [analyticsEvents])

  const clearAnalytics = useCallback(() => {
    setAnalyticsEvents([])
  }, [])

  // Track page visibility changes to hide mobile tab bar when app is backgrounded
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setBottomTabBarVisibility(false)
      } else {
        setBottomTabBarVisibility(true)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [setBottomTabBarVisibility])

  // Close mobile menu on route changes (would be handled by router events in real app)
  useEffect(() => {
    const handleRouteChange = () => {
      closeMobileMenu()
      setActiveDropdown(null)
    }

    // In a real app, you'd listen to Next.js router events here
    // router.events.on('routeChangeStart', handleRouteChange)

    return () => {
      // router.events.off('routeChangeStart', handleRouteChange)
    }
  }, [closeMobileMenu, setActiveDropdown])

  // Context value
  const contextValue: NavigationStateContextType = {
    // UI State
    uiState,
    setUIState,

    // Mobile Navigation Controls
    toggleMobileMenu,
    closeMobileMenu,
    setBottomTabBarVisibility,

    // Desktop Navigation Controls
    setActiveDropdown,
    toggleSearch,
    openSearch,
    closeSearch,

    // User Preferences
    toggleNavigationCollapse,
    setTokenDisplayPreference,
    toggleNotificationSounds,
    toggleAnimations,

    // Analytics
    trackNavigationEvent,
    getNavigationAnalytics,
    clearAnalytics
  }

  return (
    <NavigationStateContext.Provider value={contextValue}>
      {children}
    </NavigationStateContext.Provider>
  )
}

// Hook to use navigation state
export function useNavigationState() {
  const context = useContext(NavigationStateContext)

  if (!context) {
    throw new Error('useNavigationState must be used within a NavigationStateProvider')
  }

  return context
}

// Export types for use in other components
export type { NavigationUIState, NavigationAnalyticsEvent }
